import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import Nav from "../landing-pages/home-two/components/nav";
import { MTN_YELLOW } from "../../constants/Colors";
import GameOverPanel from "../../components/games/GameOverPanel";
import HapticsToggle from "../../components/games/HapticsToggle";
import { HAPTIC, haptic, stopHaptics } from "../../lib/haptics";
import {
  endOfRunMessage,
  pickPraise,
  runWentWell,
} from "../../components/games/encouragement";
import { useHideEbanqoWidget } from "../../hooks/useHideEbanqoWidget";
import {
  BAND_COLOURS,
  BAND_INTERVAL_MS,
  weaveDurationMs,
  type BandColour,
} from "../../components/games/kente/weave-config";
import type { WovenBand } from "../../components/games/kente/KenteWeave";
import {
  LIVES,
  recallBudgetMs,
  START_SPAN,
  buildSequence,
  nextSpanAfterLoss,
  nextSpanAfterWin,
  readBestScore,
  roundScore,
  GOOD_SCORE,
  pickTaunt,
  writeBestScore,
} from "../../components/games/kente/kente-rules";

/**
 * Kae Kente — Twi for "remember the kente" (Ken, 2026-09-04: "Kente
 * Sequence" read as foreign). Watch the strip weave itself, then tap the colours back in
 * order. The span grows as you get it right and eases back when you miss, so
 * the game finds the player's level in a few rounds instead of making
 * everyone start from the bottom.
 *
 * three.js is lazy-loaded, so the homepage and the campaign chat carry none
 * of it. Rules live in components/games/kente/kente-rules; the loom is
 * KenteWeave and holds no game state.
 *
 * Copy rule, and it is not optional: this is a game. Nothing here claims a
 * cognitive or medical benefit. Lumosity settled with the FTC in 2016 over
 * exactly that kind of claim, and we carry a health brand.
 */
const KenteWeave = lazy(() => import("../../components/games/kente/KenteWeave"));

type Phase = "idle" | "watching" | "recalling" | "won" | "lost" | "over";

/** Beat between the strip finishing and the player being asked for it. */
const HANDOVER_MS = 450;
/** How long the result of a round stays on screen before the next one. */
const ROUND_RESULT_MS = 1100;

export default function KaeKentePage() {
  // The site-wide widget's launcher sits over the board and the links
  // beneath it.
  useHideEbanqoWidget("kente-game");

  const [phase, setPhase] = useState<Phase>("idle");
  const [span, setSpan] = useState(START_SPAN);
  const [sequence, setSequence] = useState<BandColour[]>([]);
  const [bands, setBands] = useState<WovenBand[]>([]);
  const [markedIndex, setMarkedIndex] = useState<number | null>(null);
  const [lives, setLives] = useState(LIVES);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [round, setRound] = useState(0);
  const [best, setBest] = useState(0);
  /** Rolled when a run ends, so each game over gets its own line. */
  const [endMessage, setEndMessage] = useState("");
  /** The score as the run ends, read by the end-of-run message before
   *  writeBestScore moves the goalposts. */
  const scoreRef = useRef(0);
  /** The best as the run began. The panel's face and the end-of-run line both
   *  need it, and writeBestScore has already moved it by then. */
  const bestBeforeRun = useRef(0);
  /** playRound schedules the first tap's deadline before the round counter
   *  has re-rendered, so it reads the round from here. */
  const roundRef = useRef(1);
  const [failReason, setFailReason] = useState("Not quite");
  /** Longest strip they actually got right — the brag worth sharing. */
  const [bestSpan, setBestSpan] = useState(0);
  /** Rounds actually won. `round` counts attempts, losses included, so it
   *  cannot be used to validate a score. */
  const [roundsWon, setRoundsWon] = useState(0);
  /** The signup form stays behind the primary CTA so the offer itself is
   *  visible without scrolling — a two-field form pushed it off screen. */
  /** When the current tap runs out, and how much of its budget is left — the
   *  bar under the board reads from this. */
  const [tapDeadline, setTapDeadline] = useState<number | null>(null);
  const [tapRemaining, setTapRemaining] = useState(1);

  const timers = useRef<number[]>([]);
  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);
  const clearTimers = useCallback(() => {
    timers.current.forEach(window.clearTimeout);
    timers.current = [];
  }, []);

  // Mirrors score, so the end-of-run message can read the final value
  // without waiting for a re-render.
  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    roundRef.current = round;
  }, [round]);

  useEffect(() => {
    setBest(readBestScore());
    return clearTimers;
  }, [clearTimers]);

  /** Weaves a fresh sequence, then hands over to the player. */
  const playRound = useCallback(
    (nextSpan: number) => {
      clearTimers();
      const next = buildSequence(nextSpan);
      const startAt = performance.now() + 250;

      setSpan(nextSpan);
      setSequence(next);
      setMarkedIndex(null);
      setPhase("watching");
      // Scheduling every band up front is what makes the loom a dumb
      // renderer: it just draws whatever timestamps it is given.
      setBands(
        next.map((colour, index) => ({
          colour,
          appearAtMs: startAt + index * BAND_INTERVAL_MS,
        })),
      );

      // A tick per band, on the same schedule the loom draws them. This gives
      // the sequence a rhythm as well as a colour order — a second encoding
      // channel, for the same reason the bands carry motif ticks.
      next.forEach((_, index) => {
        later(() => haptic(HAPTIC.tick), 250 + index * BAND_INTERVAL_MS);
      });

      later(() => {
        setBands([]);
        setPhase("recalling");
        setTapDeadline(performance.now() + recallBudgetMs(0, nextSpan, roundRef.current));
      }, 250 + weaveDurationMs(nextSpan) + HANDOVER_MS);
    },
    [clearTimers, later],
  );

  const startRun = useCallback(() => {
    setScore(0);
    setStreak(0);
    setLives(LIVES);
    setRound(1);
    setBestSpan(0);
    setRoundsWon(0);
    playRound(START_SPAN);
  }, [playRound]);

  /**
   * Ends the round badly: reveals the real sequence beside where they broke,
   * spends a life, and moves on. Shared by a wrong tap and by the recall
   * clock running out, so the two behave identically — a timeout is a miss,
   * not a special case.
   */
  const failRound = useCallback(
    (position: number, why: string) => {
      clearTimers();
      setTapDeadline(null);
      const revealAt = performance.now();
      setBands(
        sequence.map((seqColour, index) => ({
          colour: seqColour,
          appearAtMs: revealAt + index * 60,
        })),
      );
      setMarkedIndex(position);
      setStreak(0);
      setFailReason(why);
      setPhase("lost");

      const livesLeft = lives - 1;
      setLives(livesLeft);

      later(() => {
        if (livesLeft <= 0) {
          stopHaptics();
          // Read the old best HERE, not inside the updater: writeBestScore runs
          // in the same batch, and an updater reading it afterwards would
          // compare the run against itself, so beating your own best could
          // never earn praise.
          const previousBest = readBestScore();
          bestBeforeRun.current = previousBest;
          setEndMessage((previous) =>
            endOfRunMessage(
              {
                score: scoreRef.current,
                goodScore: GOOD_SCORE,
                previousBest,
              },
              { praise: pickPraise, taunt: pickTaunt },
              previous,
            ),
          );
          setPhase("over");
          setScore((current) => {
            writeBestScore(current);
            setBest(readBestScore());
            return current;
          });
          return;
        }
        setRound((current) => current + 1);
        playRound(nextSpanAfterLoss(span));
      }, ROUND_RESULT_MS + sequence.length * 60);
    },
    [clearTimers, later, lives, playRound, sequence, span],
  );

  const tap = useCallback(
    (colour: BandColour) => {
      if (phase !== "recalling") return;

      const position = bands.length;
      const expected = sequence[position];

      if (colour.key !== expected.key) {
        haptic(HAPTIC.fail);
        failRound(position, "Not quite");
        return;
      }
      haptic(HAPTIC.tick);

      const woven = [...bands, { colour, appearAtMs: performance.now() }];
      setBands(woven);

      if (woven.length < sequence.length) {
        // Fresh budget for the next tap.
        setTapDeadline(performance.now() + recallBudgetMs(woven.length, span, round));
        return;
      }
      setTapDeadline(null);

      // Round complete.
      haptic(HAPTIC.success);
      const gained = roundScore(span, streak);
      const nextStreak = streak + 1;
      setBestSpan((current) => Math.max(current, span));
      setRoundsWon((current) => current + 1);
      setScore((current) => current + gained);
      setStreak(nextStreak);
      setPhase("won");
      later(() => {
        setRound((current) => current + 1);
        playRound(nextSpanAfterWin(span, nextStreak));
      }, ROUND_RESULT_MS);
    },
    [
      bands,
      failRound,
      later,
      phase,
      playRound,
      round,
      sequence,
      span,
      streak,
    ],
  );

  // The recall clock. Ticks only while a tap is outstanding, and a timeout
  // goes down the same path as a wrong answer.
  useEffect(() => {
    if (phase !== "recalling" || tapDeadline === null) return;

    const budget = recallBudgetMs(bands.length, span, round);
    const id = window.setInterval(() => {
      const left = tapDeadline - performance.now();
      setTapRemaining(Math.max(0, Math.min(1, left / budget)));
      if (left <= 0) {
        window.clearInterval(id);
        haptic(HAPTIC.fail);
        failRound(bands.length, "Too slow");
      }
    }, 80);

    return () => window.clearInterval(id);
  }, [phase, tapDeadline, bands.length, span, round, failRound]);

  const statusLine = (() => {
    switch (phase) {
      case "watching":
        return "Watch the weave…";
      case "recalling":
        return `Tap the colours in order — ${bands.length} of ${span}`;
      case "won":
        return "Correct!";
      case "lost":
        return lives > 0 ? `${failReason} — here it is` : failReason;
      case "over":
        return "Run over";
      default:
        return "Watch the strip, then tap the colours back in order.";
    }
  })();

  return (
    <div className="flex min-h-screen w-full flex-col bg-neutral-950 text-white">
      {/* text-black: this page is dark, and Nav is a light bar whose
          own labels inherit colour — without this the Log In button
          renders white on white. */}
      <div className="text-black">
        <Nav />
      </div>

      <main
        className={`mx-auto flex w-full max-w-md flex-1 flex-col px-4 sm:max-w-xl md:max-w-2xl lg:max-w-3xl py-5 ${
          // Centre the game in whatever screen it gets: these boards are a
          // fixed share of the height, and on a tall phone the leftover used
          // to pile up under the last row. Not while the run-over panel is
          // up — that content is taller than the screen, and centring an
          // overflowing column puts its top out of reach.
          phase === "over" ? "" : "justify-center"
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="m-0 text-lg font-bold sm:text-2xl">Kae Kente</h1>
            <p className="m-0 text-[11px] text-white/40 sm:text-sm">
              Remember the weave
            </p>
          </div>
          <div className="flex items-center gap-3 text-right">
            <div>
              <p className="m-0 text-[10px] uppercase tracking-wide text-white/40 sm:text-xs">
                Score
              </p>
              <p className="m-0 text-base font-bold tabular-nums sm:text-2xl">{score}</p>
            </div>
            <div className="flex gap-0.5" aria-label={`${lives} lives left`}>
              {Array.from({ length: LIVES }, (_, i) => (
                <Heart
                  key={i}
                  className="h-4 w-4"
                  strokeWidth={2.5}
                  style={{
                    color: i < lives ? MTN_YELLOW : "rgba(255,255,255,0.18)",
                    fill: i < lives ? MTN_YELLOW : "transparent",
                  }}
                  aria-hidden
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between text-[11px] text-white/45 sm:text-sm">
          <span>
            {phase === "idle" ? "Ready" : `Round ${round} · ${span} bands`}
          </span>
          <span>Best {best}</span>
        </div>

        <div className={`relative mt-3 w-full overflow-hidden rounded-2xl bg-neutral-900 ring-1 ring-white/10 ${
            phase === "over" ? "h-[22vh] min-h-[150px]" : "h-[44vh] min-h-[260px] md:h-[52vh]"
          }`}>
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-xs text-white/40 sm:text-base">
                Loading the loom…
              </div>
            }
          >
            <KenteWeave
              slotCount={span}
              bands={bands}
              markedIndex={markedIndex}
            />
          </Suspense>

          {phase === "idle" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-neutral-950/85 px-6 text-center">
              <p className="m-0 max-w-xs text-xs leading-relaxed text-white/60 sm:max-w-md sm:text-base">
                The strip weaves itself one band at a time. Tap the colours
                back in the same order.
              </p>
              <button
                type="button"
                onClick={startRun}
                className="mt-1 rounded-lg px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:brightness-95"
                style={{ backgroundColor: MTN_YELLOW }}
              >
                Start
              </button>
            </div>
          )}
        </div>

        {/* Countdown for the tap in hand. Only while one is outstanding —
            an idle bar would read as pressure that is not there. */}
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/5">
          {phase === "recalling" && tapDeadline !== null && (
            <div
              className="h-full"
              style={{
                width: `${Math.round(tapRemaining * 100)}%`,
                backgroundColor: tapRemaining < 0.3 ? "#E83B46" : MTN_YELLOW,
              }}
            />
          )}
        </div>

        <p
          className="mt-2 text-center text-xs text-white/60 sm:text-lg"
          aria-live="polite"
        >
          {statusLine}
        </p>

        {phase === "over" && (
          <GameOverPanel
            score={score}
            best={best}
            message={endMessage}
            wentWell={runWentWell({
              score,
              goodScore: GOOD_SCORE,
              previousBest: bestBeforeRun.current,
            })}
            peak={{
              value: bestSpan,
              singular: "band",
              plural: "bands",
              verb: "remembered",
            }}
            gameSlug="kae-kente"
            rounds={roundsWon}
            onPlayAgain={startRun}
          />
        )}

        {/* Colour buttons are DOM, not 3D: real tap targets, real labels, and
            the name plus tick count means the sequence never depends on
            telling red from green. */}
        <div className="mt-3 grid grid-cols-2 gap-2.5">
          {BAND_COLOURS.map((colour) => (
            <button
              key={colour.key}
              type="button"
              onClick={() => tap(colour)}
              disabled={phase !== "recalling"}
              aria-label={`${colour.label}, ${colour.ticks} ${
                colour.ticks === 1 ? "mark" : "marks"
              }`}
              className="flex items-center justify-between gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-black transition-opacity disabled:opacity-30 sm:px-6 sm:py-5 sm:text-lg"
              style={{ backgroundColor: colour.hex }}
            >
              <span
                className={
                  colour.key === "green" ||
                  colour.key === "red" ||
                  colour.key === "blue"
                    ? "text-white"
                    : undefined
                }
              >
                {colour.label}
              </span>
              <span className="flex gap-0.5" aria-hidden>
                {Array.from({ length: colour.ticks }, (_, i) => (
                  <span
                    key={i}
                    className="h-1.5 w-1.5 rounded-sm bg-black/55"
                  />
                ))}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3 text-[11px] text-white/35 sm:text-sm">
          <Link to="/play" className="underline">
            All games
          </Link>
          <HapticsToggle />
          <Link to="/play/leaderboard" className="underline">
            Leaderboard
          </Link>
        </div>
      </main>
    </div>
  );
}
